import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { selectAppDomain, selectSelectedCompanyDocument, selectSelectedCompanyOrgId, selectSelectedCompanyPartyId, useAppSelector, } from '@ibk/shared-config';
import { useGetFeatureFlagsQuery } from '@ibk/shared-feature-flags';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { MigrationContext } from '../../hooks/useBenefitMigration';
import { benefitApi } from '../../services/benefitApi';
import { benefitMigrationApi, createBenefitMigrationService, emptyMigrationSnapshot, sameMigrationScope, } from '../../services/benefitMigrationApi';
import { useCreateEmissorBenefitContractByDomain } from '../../services/emissorBenefitsApi';
import { ContractStatus } from '../../types/contract';
import { isBenefitsCompanyDocument, normalizeBenefitsDocument } from '../../utils/benefitMigrationDocument';
import { selectBenefitMigrationUserId } from '../../utils/benefitMigrationSession';
let nextGeneration = 0;
function hasMigrationInvitation(flags) {
    return (!flags.isFetching &&
        !flags.isError &&
        Boolean(flags.currentData?.featureFlags.some(flag => flag.useCase === FeatureFlagUseCase.BENEFICIOS_MIGRACAO_ENABLED &&
            flag.enabled &&
            flag.state === 'WHITELISTED')));
}
function migrationErrorReason(code) {
    if (code && ['IDENTITY_MISMATCH', 'AMBIGUOUS_MIGRATION'].includes(code))
        return 'identity-mismatch';
    if (code && ['INVALID_RESPONSE', 'INCOMPLETE_CONTRACT_SEARCH'].includes(code))
        return 'invalid-response';
    return 'network';
}
function migrationProviderState(failed, read, errorReason, errorCode) {
    if (failed)
        return { kind: 'error', reason: errorReason, code: errorCode };
    if (read)
        return { kind: 'ready', snapshot: read.snapshot };
    return { kind: 'loading' };
}
function useRetainedMigrationScope(read, userId, generation) {
    const scopeVersion = useRef({ key: '', revision: 0 });
    const scopeKey = read ? JSON.stringify([read.snapshot.identity, read.snapshot.emissorContractId]) : '';
    if (scopeVersion.current.key !== scopeKey)
        scopeVersion.current = { key: scopeKey, revision: scopeVersion.current.revision + 1 };
    const retainedScope = useRef({ key: '', value: null });
    if (retainedScope.current.key !== scopeKey) {
        retainedScope.current = {
            key: scopeKey,
            value: read
                ? { identity: read.snapshot.identity, userId, revision: scopeVersion.current.revision, generation }
                : null,
        };
    }
    return { scope: retainedScope.current.value, retainedScope };
}
function persistedMigrationRead(currentRead, discoveryFailed, lastRead, confirmedRead) {
    if (currentRead)
        return currentRead;
    if (discoveryFailed && lastRead)
        return lastRead;
    return confirmedRead;
}
function CompanyMigrationProvider({ children, userId, appDomain, organizationId, document, partyId, }) {
    const flags = useGetFeatureFlagsQuery({ partyId, userId }, { skip: !partyId, refetchOnMountOrArgChange: true });
    const invitationEnabled = hasMigrationInvitation(flags);
    const [generation] = useState(() => ++nextGeneration);
    const [confirmedRead, setConfirmedRead] = useState(null);
    const [onboardingScope, setOnboardingScope] = useState(null);
    const [createContract] = useCreateEmissorBenefitContractByDomain();
    const [refreshMigration] = benefitMigrationApi.endpoints.discoverBenefitMigration.useLazyQuery();
    const legacyContractId = confirmedRead?.snapshot.identity.legacyContractId;
    const params = useMemo(() => ({
        userId,
        appDomain,
        organizationId,
        document,
        generation,
        ...(legacyContractId ? { legacyContractId } : {}),
    }), [userId, appDomain, organizationId, document, generation, legacyContractId]);
    const lastRead = useRef(null);
    const [pollingInterval, setPollingInterval] = useState(0);
    const discovery = benefitMigrationApi.endpoints.discoverBenefitMigration.useQuery(params, {
        refetchOnMountOrArgChange: true,
        pollingInterval,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
    });
    const legacy = benefitApi.endpoints.getContractsByOrganization.useQuery({ organizationId }, {
        skip: !invitationEnabled || !discovery.currentData || Boolean(discovery.currentData.read),
        refetchOnMountOrArgChange: true,
    });
    const legacyMatches = legacy.currentData?.result.filter(contract => normalizeBenefitsDocument(contract.document) === document && contract.status === ContractStatus.ACTIVE) ?? [];
    const legacyContract = legacyMatches.length === 1 ? legacyMatches[0] : null;
    const acceptance = useMemo(() => {
        if (!invitationEnabled || !legacyContract)
            return null;
        const identity = { appDomain, organizationId, document, legacyContractId: legacyContract.id };
        const location = legacyContract.deliveryLocation;
        return {
            snapshot: { ...emptyMigrationSnapshot(identity), phase: 'acceptance-required' },
            terms: null,
            address: location
                ? {
                    street: location.address,
                    number: location.number,
                    neighborhood: location.addressLine2 ?? '',
                    postalCode: location.zipCode,
                    city: location.city,
                    state: location.state,
                    complement: location.complement,
                    recipientName: '',
                }
                : null,
        };
    }, [invitationEnabled, legacyContract, appDomain, organizationId, document]);
    if (discovery.currentData)
        lastRead.current = discovery.currentData.read;
    const persisted = persistedMigrationRead(discovery.currentData?.read, discovery.isError, lastRead.current, confirmedRead);
    useEffect(() => {
        setPollingInterval(['provisioning', 'cards-pending'].includes(persisted?.snapshot.phase ?? '') ? 5000 : 0);
    }, [persisted?.snapshot.phase]);
    const read = persisted ?? acceptance;
    const { scope, retainedScope } = useRetainedMigrationScope(read, userId, generation);
    const active = useRef(scope);
    active.current = scope;
    useEffect(() => {
        active.current = retainedScope.current.value;
        return () => {
            active.current = null;
        };
    }, [retainedScope]);
    const isCurrent = useCallback((candidate) => Boolean(active.current && sameMigrationScope(candidate, active.current)), []);
    const requestOnboarding = useCallback((candidate) => {
        if (isCurrent(candidate))
            setOnboardingScope(candidate);
    }, [isCurrent]);
    const consumeOnboarding = useCallback((candidate) => {
        setOnboardingScope(current => (current && sameMigrationScope(candidate, current) ? null : current));
    }, []);
    const refetch = useCallback(async () => {
        const candidate = active.current;
        if (!candidate)
            return null;
        const result = await refreshMigration({ ...params, legacyContractId: candidate.identity.legacyContractId });
        if (!isCurrent(candidate))
            return null;
        if (result.error)
            throw result.error;
        if (result.data?.read)
            setConfirmedRead(result.data.read);
        return result.data?.read ?? confirmedRead ?? acceptance;
    }, [refreshMigration, params, isCurrent, confirmedRead, acceptance]);
    const service = useMemo(() => createBenefitMigrationService(refetch, isCurrent, {
        create: body => createContract(body).unwrap(),
        onCreated: setConfirmedRead,
    }), [refetch, isCurrent, createContract]);
    const initializing = (!persisted && (discovery.isLoading || (!discovery.currentData && !discovery.isError))) ||
        (invitationEnabled && !persisted && !legacy.currentData && !legacy.isError);
    const failed = discovery.isError || (invitationEnabled && !persisted && (legacy.isError || legacyMatches.length > 1));
    const enabled = Boolean(persisted || acceptance || failed);
    const backendError = discovery.error;
    const errorReason = migrationErrorReason(backendError?.code);
    const errorCode = backendError?.code ?? (legacyMatches.length > 1 ? 'AMBIGUOUS_LEGACY_CONTRACT' : undefined);
    const safeRefetch = useCallback(async () => {
        try {
            return await refetch();
        }
        catch {
            return null;
        }
    }, [refetch]);
    const contextValue = useMemo(() => ({
        enabled,
        initializing,
        read,
        scope,
        service,
        isCurrent,
        onboarding: {
            requested: Boolean(scope && onboardingScope && sameMigrationScope(scope, onboardingScope)),
            request: requestOnboarding,
            consume: consumeOnboarding,
        },
        refetch: safeRefetch,
        state: migrationProviderState(failed, read, errorReason, errorCode),
    }), [
        enabled,
        initializing,
        read,
        scope,
        service,
        isCurrent,
        onboardingScope,
        requestOnboarding,
        consumeOnboarding,
        safeRefetch,
        failed,
        errorReason,
        errorCode,
    ]);
    return _jsx(MigrationContext.Provider, { value: contextValue, children: children });
}
export function BenefitMigrationProvider({ children }) {
    const appDomain = useAppSelector(selectAppDomain);
    const userId = useAppSelector(selectBenefitMigrationUserId);
    const organizationId = useAppSelector(selectSelectedCompanyOrgId);
    const document = normalizeBenefitsDocument(useAppSelector(selectSelectedCompanyDocument) ?? '');
    const partyId = useAppSelector(selectSelectedCompanyPartyId) ?? '';
    if (!userId || !organizationId || !isBenefitsCompanyDocument(document))
        return _jsx(_Fragment, { children: children });
    return (_jsx(CompanyMigrationProvider, { userId: userId, appDomain: appDomain, organizationId: organizationId, document: document, partyId: partyId, children: children }, JSON.stringify([userId, appDomain, organizationId, document, partyId])));
}
